"use client";
import React, { useReducer, useEffect, useRef, useCallback, useState } from 'react';
import { BsSearch, BsCalendar3, BsPersonCircle } from 'react-icons/bs';
import { AiOutlineMenu, AiOutlineClose } from 'react-icons/ai';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import Button from '../components/button';

// Reducer pour gérer l'état de la navbar
const navbarReducer = (state, action) => {
    switch (action.type) {
        case 'TOGGLE_DROPDOWN':
            return { ...state, dropdownOpen: !state.dropdownOpen };
        case 'TOGGLE_MENU':
            return { ...state, menuOpen: !state.menuOpen };
        case 'TOGGLE_SEARCH':
            return { ...state, searchOpen: !state.searchOpen };
        case 'SET_SEARCH_TERM':
            return { ...state, searchTerm: action.payload };
        case 'CLOSE_ALL':
            return { ...state, dropdownOpen: false, menuOpen: false, searchOpen: false };
        case 'CLOSE_DROPDOWN':
            return { ...state, dropdownOpen: false };
        case 'CLOSE_MENU':
            return { ...state, menuOpen: false };
        default:
            return state;
    }
};

const initialState = {
    dropdownOpen: false,
    menuOpen: false,
    searchOpen: false,
    searchTerm: ''
};

const navLinks = [
    { href: '/accompagnement', label: 'Accompagnement' },
    { href: '/rendez-vous-et-tarifs', label: 'Rendez-vous et tarifs' },
    { href: '/articles', label: 'Articles' },
    { href: '/a-propos', label: 'A propos' },
];

const sphereItems = [
    {
        href: '/fertilite',
        label: 'Fertilité',
        description: 'Accompagnement naturel pour la conception',
        icon: '🌱',
        colorFrom: 'from-emerald-100',
        colorTo: 'to-emerald-200',
        hoverFrom: 'group-hover:from-emerald-200',
        hoverTo: 'group-hover:to-emerald-300',
        textColor: 'text-emerald-600',
    },
    {
        href: '/troubles-hormonaux',
        label: 'Troubles hormonaux',
        description: 'Équilibre hormonal et bien-être',
        icon: '⚖️',
        colorFrom: 'from-purple-100',
        colorTo: 'to-purple-200',
        hoverFrom: 'group-hover:from-purple-200',
        hoverTo: 'group-hover:to-purple-300',
        textColor: 'text-purple-600',
    },
    {
        href: '/perte-de-poids',
        label: 'Perte de poids',
        description: 'Approche naturelle et durable',
        icon: '🌿',
        colorFrom: 'from-green-100',
        colorTo: 'to-green-200',
        hoverFrom: 'group-hover:from-green-200',
        hoverTo: 'group-hover:to-green-300',
        textColor: 'text-green-600',
    },
];

const Navbar = () => {
    const [state, dispatch] = useReducer(navbarReducer, initialState);
    const { dropdownOpen, menuOpen, searchOpen, searchTerm } = state;
    const [scrolled, setScrolled] = useState(false);

    const router = useRouter();
    const pathname = usePathname();
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const menuRef = useRef(null);

    // Détection du scroll pour l'effet glass
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Vérifie si un lien est actif
    const isActive = useCallback((href) => {
        if (href === '/') return pathname === '/';
        return pathname.startsWith(href);
    }, [pathname]);

    // Vérifie si le dropdown "Sphère d'action" contient la page active
    const isSphereActive = useCallback(() => {
        return sphereItems.some(item => pathname.startsWith(item.href));
    }, [pathname]);

    // Actions optimisées avec useCallback
    const toggleDropdown = useCallback(() => {
        dispatch({ type: 'TOGGLE_DROPDOWN' });
    }, []);

    const toggleMenu = useCallback(() => {
        dispatch({ type: 'TOGGLE_MENU' });
    }, []);

    const toggleSearch = useCallback(() => {
        dispatch({ type: 'TOGGLE_SEARCH' });
    }, []);

    const closeMenu = useCallback(() => {
        dispatch({ type: 'CLOSE_MENU' });
    }, []);

    // Navigation améliorée avec Next.js router
    const handleSearchSubmit = useCallback((event) => {
        event.preventDefault();
        if (searchTerm.trim()) {
            router.push(`/search?query=${encodeURIComponent(searchTerm.trim())}`);
            dispatch({ type: 'CLOSE_ALL' });
        }
    }, [searchTerm, router]);

    // Gestion des clics extérieurs optimisée
    const handleClickOutside = useCallback((event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
            !event.target.closest('[data-dropdown-toggle]')) {
            dispatch({ type: 'CLOSE_DROPDOWN' });
        }
        if (menuRef.current && !menuRef.current.contains(event.target) &&
            !event.target.closest('[data-menu-toggle]')) {
            dispatch({ type: 'CLOSE_MENU' });
        }
    }, []);

    // Gestion du clavier pour l'accessibilité
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            dispatch({ type: 'CLOSE_ALL' });
        }
        if (event.key === 'Tab' && menuOpen) {
            const menuElement = menuRef.current;
            if (menuElement) {
                const focusableElements = menuElement.querySelectorAll(
                    'a, button, input, [tabindex]:not([tabindex="-1"])'
                );
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (event.shiftKey && document.activeElement === firstElement) {
                    event.preventDefault();
                    lastElement?.focus();
                } else if (!event.shiftKey && document.activeElement === lastElement) {
                    event.preventDefault();
                    firstElement?.focus();
                }
            }
        }
    }, [menuOpen]);

    // Bloquer le scroll du body quand le menu mobile est ouvert
    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    // Effects optimisés avec dépendances
    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleClickOutside, handleKeyDown]);

    // Focus automatique sur l'input de recherche
    useEffect(() => {
        if (searchOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [searchOpen]);

    return (
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            scrolled
                ? 'bg-white/80 backdrop-blur-md shadow-sm'
                : 'bg-transparent'
        }`}>
            <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
                <div className="flex justify-between items-center h-20">
                    {/* Logo */}
                    <Link href="/" aria-label="Retour à la page d'accueil" className="relative w-48 h-16 flex-shrink-0 transition-transform duration-200 hover:scale-105">
                        <Image
                            src="/assets/logohormelys1.webp"
                            alt="Hormelys — naturopathie à Gignac"
                            fill
                            sizes="(max-width: 768px) 150px, 200px"
                            style={{ objectFit: 'contain' }}
                            priority
                        />
                    </Link>

                    {/* Menu Links desktop */}
                    <ul className="hidden xl:flex items-center space-x-6 whitespace-nowrap" aria-label="Menu principal">
                        {/* Dropdown Sphère d'action */}
                        <li className="relative">
                            {dropdownOpen && (
                                <div
                                    className="fixed inset-0 bg-black/10 backdrop-blur-[2px] z-40 transition-all duration-300"
                                    onClick={() => dispatch({ type: 'CLOSE_DROPDOWN' })}
                                />
                            )}

                            <button
                                onClick={toggleDropdown}
                                aria-expanded={dropdownOpen}
                                aria-haspopup="true"
                                data-dropdown-toggle
                                className={`text-sm lg:text-[15px] leading-relaxed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#2C6E63] rounded px-2 py-1 ${
                                    isSphereActive()
                                        ? 'text-[#2C6E63] font-medium'
                                        : 'text-black hover:text-[#2C6E63]'
                                }`}
                            >
                                Sphère d&apos;action
                                <span className={`ml-1 text-xs transition-transform duration-300 ease-in-out inline-block ${dropdownOpen ? 'rotate-180' : 'rotate-0'}`}>
                                    ▼
                                </span>
                            </button>
                            <ul
                                ref={dropdownRef}
                                className={`transition-all duration-300 ease-out transform origin-top ${
                                    dropdownOpen
                                        ? 'block opacity-100 scale-100 translate-y-0'
                                        : 'hidden opacity-0 scale-95 -translate-y-2'
                                } mt-4 absolute top-full left-0 w-72 bg-white shadow-xl rounded-xl border border-gray-100 z-50`}
                                role="menu"
                                style={{
                                    background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                                }}
                            >
                                    {sphereItems.map((item) => (
                                        <li key={item.href} className="group p-1" role="menuitem">
                                            <Link
                                                href={item.href}
                                                onClick={closeMenu}
                                                className={`flex items-start space-x-3 px-4 py-3 hover:bg-gradient-to-r hover:from-[#2C6E63]/10 hover:to-[#2C6E63]/5 cursor-pointer transition-all duration-200 rounded-xl group-hover:shadow-sm ${
                                                    isActive(item.href) ? 'text-[#2C6E63] bg-[#2C6E63]/5' : 'text-black hover:text-[#2C6E63]'
                                                }`}
                                            >
                                                <div className="flex-shrink-0 mt-0.5">
                                                    <div className={`w-8 h-8 bg-gradient-to-br ${item.colorFrom} ${item.colorTo} rounded-lg flex items-center justify-center ${item.hoverFrom} ${item.hoverTo} transition-colors duration-200`}>
                                                        <span className={`${item.textColor} text-lg`}>{item.icon}</span>
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-semibold text-sm leading-tight">
                                                        {item.label}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-1 leading-relaxed whitespace-normal">
                                                        {item.description}
                                                    </p>
                                                </div>
                                            </Link>
                                        </li>
                                    ))}
                                <div className="absolute -top-2 left-6 w-4 h-4 bg-white border-l border-t border-gray-100 transform rotate-45"></div>
                            </ul>
                        </li>

                        {navLinks.map((link) => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    className={`text-sm lg:text-[15px] leading-relaxed transition-all duration-200 px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-[#2C6E63] ${
                                        isActive(link.href)
                                            ? 'text-[#2C6E63] font-medium'
                                            : 'text-black hover:text-[#2C6E63]'
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    {/* Actions desktop : recherche + CTA */}
                    <div className="hidden xl:flex items-center space-x-4 whitespace-nowrap">
                        <div className="relative">
                            <button
                                onClick={toggleSearch}
                                aria-label="Rechercher"
                                className="text-lg text-black hover:text-[#2C6E63] cursor-pointer transition-colors duration-200 p-2"
                            >
                                <BsSearch />
                            </button>
                            {searchOpen && (
                                <form onSubmit={handleSearchSubmit} className="absolute top-full right-0 mt-3 z-50">
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        placeholder="Recherche..."
                                        className="border border-[#D4DDD1] focus:border-[#2C6E63] outline-none px-4 py-2 w-[220px] bg-white shadow-lg rounded-lg transition-colors duration-200"
                                        value={searchTerm}
                                        onChange={(e) => dispatch({ type: 'SET_SEARCH_TERM', payload: e.target.value })}
                                        aria-label="Champ de recherche"
                                    />
                                </form>
                            )}
                        </div>
                        <Link
                            href="/login"
                            className={`flex items-center gap-1.5 text-sm transition-all duration-200 px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-[#2C6E63] ${
                                isActive('/login')
                                    ? 'text-[#2C6E63] font-medium'
                                    : 'text-black hover:text-[#2C6E63]'
                            }`}
                        >
                            <BsPersonCircle className="text-base" />
                            Se connecter
                        </Link>
                        <Link href="/reservation" aria-label="Prendre RDV">
                            <Button shiny className="flex items-center gap-2 !px-5 !py-2.5 !text-sm">
                                <BsCalendar3 className="text-sm" />
                                Prendre RDV
                            </Button>
                        </Link>
                    </div>

                    {/* Burger mobile */}
                    <button
                        data-menu-toggle
                        onClick={toggleMenu}
                        aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
                        aria-expanded={menuOpen}
                        className="xl:hidden text-2xl text-black cursor-pointer hover:text-[#2C6E63] transition-colors duration-200 p-2 z-50"
                    >
                        {menuOpen ? <AiOutlineClose /> : <AiOutlineMenu />}
                    </button>
                </div>
            </div>

            {/* Menu mobile — slide-in depuis la droite */}
            <div
                ref={menuRef}
                className={`fixed inset-0 top-0 xl:hidden transition-all duration-300 ease-in-out ${
                    menuOpen ? 'visible' : 'invisible'
                }`}
            >
                {/* Overlay */}
                <div
                    className={`absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-300 ${
                        menuOpen ? 'opacity-100' : 'opacity-0'
                    }`}
                    onClick={closeMenu}
                />

                {/* Panel */}
                <div className={`absolute top-0 right-0 w-full max-w-sm h-full bg-white/95 backdrop-blur-md shadow-2xl transition-transform duration-300 ease-in-out ${
                    menuOpen ? 'translate-x-0' : 'translate-x-full'
                }`}>
                    <div className="flex flex-col h-full pt-24 px-6 pb-8 overflow-y-auto">
                        {/* Sphère d'action mobile */}
                        <div className="mb-2">
                            <button
                                onClick={toggleDropdown}
                                aria-expanded={dropdownOpen}
                                data-dropdown-toggle
                                className={`w-full flex items-center justify-between text-lg font-medium py-3 transition-colors duration-200 ${
                                    isSphereActive() ? 'text-[#2C6E63]' : 'text-black'
                                }`}
                            >
                                Sphère d&apos;action
                                <span className={`text-xs transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : 'rotate-0'}`}>
                                    ▼
                                </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ease-out ${
                                dropdownOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                            }`}>
                                <div className="pl-2 space-y-1 pb-2">
                                    {sphereItems.map((item) => (
                                        <Link
                                            key={item.href}
                                            href={item.href}
                                            onClick={closeMenu}
                                            className={`flex items-center space-x-3 px-3 py-3 rounded-xl transition-all duration-200 ${
                                                isActive(item.href)
                                                    ? 'text-[#2C6E63] bg-[#2C6E63]/10'
                                                    : 'text-gray-700 hover:text-[#2C6E63] hover:bg-[#2C6E63]/5'
                                            }`}
                                        >
                                            <div className={`w-8 h-8 bg-gradient-to-br ${item.colorFrom} ${item.colorTo} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                                <span className={`${item.textColor} text-base`}>{item.icon}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm">{item.label}</p>
                                                <p className="text-xs text-gray-500">{item.description}</p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Liens principaux mobile */}
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                onClick={closeMenu}
                                className={`text-lg py-3 border-b border-gray-100 transition-colors duration-200 ${
                                    isActive(link.href)
                                        ? 'text-[#2C6E63] font-medium'
                                        : 'text-black hover:text-[#2C6E63]'
                                }`}
                            >
                                {link.label}
                            </Link>
                        ))}

                        {/* Recherche mobile */}
                        <form onSubmit={handleSearchSubmit} className="mt-6">
                            <div className="relative">
                                <BsSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Rechercher un article..."
                                    className="w-full pl-10 pr-4 py-3 border border-[#D4DDD1] rounded-xl bg-white focus:border-[#2C6E63] outline-none transition-colors duration-200"
                                    value={searchTerm}
                                    onChange={(e) => dispatch({ type: 'SET_SEARCH_TERM', payload: e.target.value })}
                                    aria-label="Champ de recherche"
                                />
                            </div>
                        </form>

                        {/* CTA mobile */}
                        <div className="mt-auto pt-6 space-y-3">
                            <Link
                                href="/login"
                                onClick={closeMenu}
                                className={`flex items-center justify-center gap-2 w-full py-3 rounded-xl border transition-all duration-200 ${
                                    isActive('/login')
                                        ? 'border-[#2C6E63] text-[#2C6E63] bg-[#2C6E63]/5'
                                        : 'border-gray-300 text-gray-700 hover:border-[#2C6E63] hover:text-[#2C6E63]'
                                }`}
                            >
                                <BsPersonCircle className="text-lg" />
                                Se connecter
                            </Link>
                            <Link href="/reservation" onClick={closeMenu} className="block">
                                <Button shiny className="w-full flex items-center justify-center gap-2">
                                    <BsCalendar3 />
                                    Prendre rendez-vous
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
